import rawContent from '../../noorsher-zahnarzt-content.json';
import type { SiteContent } from './types';

const requiredObjectKeys: Array<keyof SiteContent> = [
  'meta',
  'brand',
  'theme',
  'seo',
  'navigation',
  'topbar',
  'contact',
  'openingHours',
  'hero',
  'trustStrip',
  'introSection',
  'servicesSection',
  'whyUsSection',
  'processSection',
  'reviewsSection',
  'faqSection',
  'chatbot',
  'teamPage',
  'praxisPage',
  'contactPage',
  'placeholderPages',
  'footer',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function assertString(value: unknown, path: string): asserts value is string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`Invalid content: ${path} must be a non-empty string.`);
  }
}

function assertArray(value: unknown, path: string): asserts value is unknown[] {
  if (!Array.isArray(value)) {
    throw new Error(`Invalid content: ${path} must be an array.`);
  }
}

function assertContentShape(content: unknown): asserts content is SiteContent {
  if (!isRecord(content)) {
    throw new Error('Invalid content: root must be an object.');
  }

  const record = content as Record<string, unknown>;

  requiredObjectKeys.forEach((key) => {
    if (!(key in record)) {
      throw new Error(`Invalid content: missing ${String(key)}.`);
    }
  });

  const brand = record.brand as Record<string, unknown>;
  const hero = record.hero as Record<string, unknown>;
  const navigation = record.navigation as Record<string, unknown>;
  const servicesSection = record.servicesSection as Record<string, unknown>;
  const reviewsSection = record.reviewsSection as Record<string, unknown>;
  const faqSection = record.faqSection as Record<string, unknown>;
  const openingHours = record.openingHours as Record<string, unknown>;

  assertString(brand.name, 'brand.name');
  assertString(brand.shortName, 'brand.shortName');
  assertString(hero.headline, 'hero.headline');
  assertString(hero.subheadline, 'hero.subheadline');
  assertArray(navigation.primary, 'navigation.primary');
  assertArray(servicesSection.services, 'servicesSection.services');
  assertArray(reviewsSection.highlights, 'reviewsSection.highlights');
  assertArray(reviewsSection.allReviews, 'reviewsSection.allReviews');
  assertArray(faqSection.items, 'faqSection.items');
  assertArray(openingHours.days, 'openingHours.days');
}

export function loadContent(): SiteContent {
  const cloned = structuredClone(rawContent);
  assertContentShape(cloned);
  return cloned;
}

export const siteContent = loadContent();

export function getNavigationLabelByHref(content: SiteContent, href: string): string {
  return content.navigation.primary.find((item) => item.href === href)?.label ?? content.brand.shortName;
}
